import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import API from '../ApiInterface';
import ApplicationDetails from './ApplicationDetails';
import OnboardSDKPartner from './OnboardSDKPartner';
import OnboardPartner from './OnboardPartner';
import PartnerDetails from './PartnerDetails';
import PartnerApplicationEditor from './PartnerApplicationEditor';
import SDKAppForm from './SDKAppForm';
import { buildOverrides, createSDKApp, hydrateSDKApp, isSDKAppValid } from './sdkPartnerUtils';
import { detailCardClass, detailItemClass, linkButtonClass, primaryButtonClass, secondaryButtonClass, sectionTitleClass, statusPillClass, tableHeaderClass, tableShellClass } from './ui';

const formatPacificDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);

  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Los_Angeles',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
    timeZoneName: 'short',
  }).format(date);
};

const toRows = (partners) => {
  return partners.map((partner) => ({
    rowKey: partner.partnerId || partner.userId || 'partner',
    partnerName: partner.name || '-',
    partnerDetails: {
      partnerId: partner.partnerId || '-',
      userId: partner.userId || '-',
      displayInDirectory: Array.isArray(partner.applications) && partner.applications.some((application) => application?.displayInDirectory)
        ? 'Yes'
        : 'No',
      hourlyLimit: partner?.limits?.hourly ?? '-',
      monthlyLimit: partner?.limits?.monthly ?? '-',
      partnerCreatedAt: formatPacificDateTime(partner.createdAt),
      partnerUpdatedAt: formatPacificDateTime(partner.updatedAt),
      partnerDeletedAt: formatPacificDateTime(partner.deletedAt),
    },
    applications: Array.isArray(partner.applications) ? partner.applications : [],
  }));
};

const toSdkRows = (partners) => {
  return partners.map((partner) => ({
    rowKey: partner.partnerId || partner.name || 'sdk-partner',
    partnerName: partner.name || '-',
    partnerDetails: {
      partnerId: partner.partnerId || '-',
      name: partner.name || '-',
      active: typeof partner.active === 'boolean' ? partner.active : '-',
      createdAt: partner.createdAt || '-',
      updatedAt: partner.updatedAt || '-',
      deletedAt: partner.deletedAt || '-',
    },
    apps: Array.isArray(partner.apps) ? partner.apps : [],
  }));
};

const labelFromKey = (key) => {
  if (!key) return key;
  return key.replace(/([A-Z])/g, ' $1').replace(/^./, (char) => char.toUpperCase());
};

const formatFieldValue = (value) => {
  if (value === null || typeof value === 'undefined') return '-';
  if (Array.isArray(value)) return value.length ? value.join(', ') : '[]';
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

const formatSdkOverrides = (overrides) => {
  if (!overrides) return '-';

  const lines = [];

  if (Array.isArray(overrides.add)) {
    overrides.add.forEach((item) => {
      if (!item?.sdkFeature || !item?.sdkDeviceFeature) return;
      lines.push(`Add ${item.sdkDeviceFeature} to ${item.sdkFeature}`);
    });
  }

  if (Array.isArray(overrides.remove)) {
    overrides.remove.forEach((item) => {
      if (!item?.sdkFeature || !item?.sdkDeviceFeature) return;
      lines.push(`Remove ${item.sdkDeviceFeature} from ${item.sdkFeature}`);
    });
  }

  return lines.length ? lines : '-';
};

const isUuid = (value) =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    String(value || '')
  );

const DetailsCard = ({ title, data }) => (
  <div className={detailCardClass}>
    <h4 className={`mb-2 ${sectionTitleClass}`}>{title}</h4>
    <div className="grid grid-cols-1 gap-2 text-xs text-slate-700 md:grid-cols-2">
      {Object.entries(data || {}).map(([key, value]) => (
        <div key={key} className={detailItemClass}>
          <span className="font-medium text-slate-800">{labelFromKey(key)}:</span>{' '}
          <span className={`text-slate-700 ${isUuid(value) ? 'font-mono' : ''}`}>
            {key.endsWith('At') ? formatPacificDateTime(value) : formatFieldValue(value)}
          </span>
        </div>
      ))}
    </div>
  </div>
);

const SDKPartnerDetails = ({ details, onToggleActive, onAddApp, toggling }) => (
  <div className={detailCardClass}>
    <div className="mb-2 flex items-center justify-between gap-3">
      <h4 className={sectionTitleClass}>SDK Partner Details</h4>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={onAddApp}
          className={secondaryButtonClass}
        >
          Add App
        </button>
        <button
          type="button"
          onClick={onToggleActive}
          disabled={toggling}
          className={secondaryButtonClass}
        >
          {toggling ? 'Saving...' : `Set ${details?.active === true ? 'Inactive' : 'Active'}`}
        </button>
      </div>
    </div>
    <div className="grid grid-cols-1 gap-2 text-xs text-slate-700 md:grid-cols-2">
      {Object.entries(details || {}).map(([key, value]) => (
        <div key={key} className={detailItemClass}>
          <span className="font-medium text-slate-800">{labelFromKey(key)}:</span>{' '}
          <span className={`text-slate-700 ${isUuid(value) ? 'font-mono' : ''}`}>
            {key.endsWith('At') ? formatPacificDateTime(value) : formatFieldValue(value)}
          </span>
        </div>
      ))}
    </div>
  </div>
);

const SDKAppDetails = ({ app, onFeatureSetClick, onUpdateApp }) => (
  <div className={detailCardClass}>
    <h4 className={`mb-2 ${sectionTitleClass}`}>SDK App Details</h4>
    <div className="grid grid-cols-1 gap-2 text-xs text-slate-700 md:grid-cols-2">
      {Object.entries(app || {}).map(([key, value]) => (
        <div key={key} className={detailItemClass}>
          <span className="font-medium text-slate-800">{labelFromKey(key)}:</span>{' '}
          {key === 'featureSetId' && value ? (
            <button
              type="button"
              onClick={onFeatureSetClick}
              className="font-mono text-xs text-blue-700 hover:text-blue-900 hover:underline"
            >
              {String(value)}
            </button>
          ) : key === 'overrides' ? (
            Array.isArray(formatSdkOverrides(value)) ? (
              <div className="mt-1 space-y-1">
                {formatSdkOverrides(value).map((line) => (
                  <div key={line} className="text-slate-700">
                    {line}
                  </div>
                ))}
              </div>
            ) : (
              <span className="text-slate-700">{formatSdkOverrides(value)}</span>
            )
          ) : (
            <span className={`text-slate-700 ${isUuid(value) ? 'font-mono' : ''}`}>
              {key.endsWith('At') ? formatPacificDateTime(value) : formatFieldValue(value)}
            </span>
          )}
        </div>
      ))}
    </div>
    <div className="mt-3 flex gap-2">
      <button
        type="button"
        onClick={onUpdateApp}
        className={secondaryButtonClass}
      >
        Update App
      </button>
    </div>
  </div>
);

const SDKFeatureSetDetails = ({ featureSet }) => {
  const [expandedFeatureId, setExpandedFeatureId] = useState('');
  const { features = [], ...topLevelData } = featureSet || {};

  return (
    <div className={detailCardClass}>
      <h4 className={`mb-2 ${sectionTitleClass}`}>SDK Feature Set Details</h4>
      <div className="grid grid-cols-1 gap-2 text-xs text-slate-700 md:grid-cols-2">
        {Object.entries(topLevelData || {}).map(([key, value]) => (
          <div key={key} className={detailItemClass}>
            <span className="font-medium text-slate-800">{labelFromKey(key)}:</span>{' '}
            <span className={`text-slate-700 ${isUuid(value) ? 'font-mono' : ''}`}>
              {key.endsWith('At') ? formatPacificDateTime(value) : formatFieldValue(value)}
            </span>
          </div>
        ))}
      </div>

      <div className="mt-3 rounded bg-white px-2 py-2">
        <div className="mb-2 text-xs font-medium text-slate-800">Features</div>
        {features.length > 0 ? (
          <div className="space-y-2">
            {features.map((feature, index) => {
              const featureKey = feature?.id || `feature-${index}`;
              return (
                <div key={featureKey}>
                  <button
                    type="button"
                    onClick={() => setExpandedFeatureId((prev) => (prev === featureKey ? '' : featureKey))}
                    className="text-left text-xs font-semibold text-blue-700 hover:text-blue-900 hover:underline"
                  >
                    {feature?.id || '-'}
                  </button>

                  {expandedFeatureId === featureKey && (
                    <div className="mt-2 grid grid-cols-1 gap-2 md:grid-cols-2">
                      {Object.entries(feature || {}).map(([key, value]) => (
                        <div key={key} className={detailItemClass}>
                          <span className="font-medium text-slate-800">{labelFromKey(key)}:</span>{' '}
                          <span className="text-slate-700">{formatFieldValue(value)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-xs text-slate-500">No features found.</div>
        )}
      </div>
    </div>
  );
};

const TabPanel = ({ children, value, index }) => {
  if (value !== index) return null;
  return <div>{children}</div>;
};

const PAGE_SIZE = 25;

const ManagePartners = (props) => {
  const [partners, setPartners] = useState([]);
  const [sdkPartners, setSdkPartners] = useState([]);
  const [sdkFeatureSets, setSdkFeatureSets] = useState([]);
  const [partnersPage, setPartnersPage] = useState(0);
  const [sdkPartnersPage, setSdkPartnersPage] = useState(0);
  const [partnersHasNextPage, setPartnersHasNextPage] = useState(false);
  const [sdkPartnersHasNextPage, setSdkPartnersHasNextPage] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sdkLoading, setSdkLoading] = useState(true);
  const [error, setError] = useState('');
  const [sdkError, setSdkError] = useState('');
  const [expandedApplicationKey, setExpandedApplicationKey] = useState('');
  const [expandedPartnerKey, setExpandedPartnerKey] = useState('');
  const [expandedSdkAppKey, setExpandedSdkAppKey] = useState('');
  const [expandedSdkPartnerKey, setExpandedSdkPartnerKey] = useState('');
  const [expandedSdkFeatureSetKey, setExpandedSdkFeatureSetKey] = useState('');
  const [partnerAppEditor, setPartnerAppEditor] = useState(null);
  const [partnerAppActionError, setPartnerAppActionError] = useState('');
  const [partnerAppActionSaving, setPartnerAppActionSaving] = useState(false);
  const [sdkAppEditor, setSdkAppEditor] = useState(null);
  const [sdkTogglePartnerId, setSdkTogglePartnerId] = useState('');
  const [sdkActionError, setSdkActionError] = useState('');
  const [sdkActionSaving, setSdkActionSaving] = useState(false);
  const [activeMainTab, setActiveMainTab] = useState(0);
  const [activePartnersTab, setActivePartnersTab] = useState(0);
  const [activeSdkTab, setActiveSdkTab] = useState(0);
  const [applicationPartner, setApplicationPartner] = useState(null);

  const token =
    props?.location?.state?.userInfo?.token ||
    props?.location?.state?.token ||
    '';
  const permissions = props?.location?.state?.userInfo?.permissions || [];
  const canManagePartners = permissions.includes('MP');
  const canManageSdkPartners = permissions.includes('MSP');
  const mainTabs = [
    canManagePartners ? { key: 'partners', label: 'Partners' } : null,
    canManageSdkPartners ? { key: 'sdk', label: 'SDK Partners' } : null,
  ].filter(Boolean);

  const fetchPartners = useCallback(async (isMountedRef = { current: true }) => {
    if (!canManagePartners) {
      if (isMountedRef.current) {
        setPartners([]);
        setLoading(false);
      }
      return;
    }

    if (!token) {
      if (isMountedRef.current) {
        setError('Missing token. Open this page from the dashboard menu after signing in.');
        setLoading(false);
      }
      return;
    }

    setLoading(true);
    setError('');

    const response = await API.getAllPartners(token, PAGE_SIZE, partnersPage * PAGE_SIZE);

    if (!isMountedRef.current) return;

    if (response.status >= 200 && response.status < 300) {
      const nextPartners = Array.isArray(response.data?.partners) ? response.data.partners : [];
      setPartners(nextPartners);
      setPartnersHasNextPage(nextPartners.length === PAGE_SIZE);
    } else {
      const message = response?.data?.errorDescription || response?.data?.message || 'Failed to load partners.';
      setError(message);
    }

    setLoading(false);
  }, [canManagePartners, partnersPage, token]);

  const fetchSdkPartners = useCallback(async (isMountedRef = { current: true }) => {
    if (!canManageSdkPartners) {
      if (isMountedRef.current) {
        setSdkPartners([]);
        setSdkLoading(false);
      }
      return;
    }

    if (!token) {
      if (isMountedRef.current) {
        setSdkError('Missing token. Open this page from the dashboard menu after signing in.');
        setSdkLoading(false);
      }
      return;
    }

    setSdkLoading(true);
    setSdkError('');

    const response = await API.getSDKPartners(token, PAGE_SIZE, sdkPartnersPage * PAGE_SIZE);

    if (!isMountedRef.current) return;

    if (response.status >= 200 && response.status < 300) {
      const nextSdkPartners = Array.isArray(response.data?.partners) ? response.data.partners : [];
      setSdkPartners(nextSdkPartners);
      setSdkPartnersHasNextPage(nextSdkPartners.length === PAGE_SIZE);
    } else {
      const message = response?.data?.errorDescription || response?.data?.message || 'Failed to load SDK partners.';
      setSdkError(message);
    }

    setSdkLoading(false);
  }, [canManageSdkPartners, sdkPartnersPage, token]);

  const fetchSdkFeatureSets = useCallback(async (isMountedRef = { current: true }) => {
    if (!canManageSdkPartners) {
      if (isMountedRef.current) {
        setSdkFeatureSets([]);
      }
      return;
    }

    if (!token) {
      return;
    }

    const response = await API.getSDKFeatureSets(token);
    if (!isMountedRef.current) return;

    if (response.status >= 200 && response.status < 300) {
      setSdkFeatureSets(Array.isArray(response.data?.sdkFeatureSets) ? response.data.sdkFeatureSets : []);
    } else {
      const message = response?.data?.errorDescription || response?.data?.message || 'Failed to load SDK feature sets.';
      setSdkError((prev) => prev || message);
    }
  }, [canManageSdkPartners, token]);

  useEffect(() => {
    const isMounted = { current: true };

    fetchPartners(isMounted);
    fetchSdkPartners(isMounted);
    fetchSdkFeatureSets(isMounted);

    return () => {
      isMounted.current = false;
    };
  }, [fetchPartners, fetchSdkFeatureSets, fetchSdkPartners]);

  useEffect(() => {
    if (mainTabs.length === 0) {
      return;
    }

    const currentMainTabKey = mainTabs[activeMainTab]?.key;
    if (!currentMainTabKey) {
      setActiveMainTab(0);
      return;
    }

    if (currentMainTabKey === 'partners' && !canManagePartners) {
      setActiveMainTab(mainTabs.findIndex((tab) => tab.key === 'sdk'));
    }

    if (currentMainTabKey === 'sdk' && !canManageSdkPartners) {
      setActiveMainTab(mainTabs.findIndex((tab) => tab.key === 'partners'));
    }
  }, [activeMainTab, canManagePartners, canManageSdkPartners, mainTabs]);

  const rows = useMemo(() => toRows(partners), [partners]);
  const sdkRows = useMemo(() => toSdkRows(sdkPartners), [sdkPartners]);
  const sdkFeatureSetMap = useMemo(() => {
    return sdkFeatureSets.reduce((accumulator, featureSet) => {
      accumulator[featureSet.featureSetId] = featureSet;
      return accumulator;
    }, {});
  }, [sdkFeatureSets]);
  const sdkFeatureCatalog = useMemo(() => {
    const sdkFeatureSet = new Set();
    const sdkDeviceFeatureSet = new Set();

    sdkFeatureSets.forEach((featureSet) => {
      (featureSet.features || []).forEach((feature) => {
        if (feature?.id) {
          sdkFeatureSet.add(feature.id);
        }

        (feature.deviceFeatures || []).forEach((deviceFeature) => {
          if (deviceFeature) {
            sdkDeviceFeatureSet.add(deviceFeature);
          }
        });
      });
    });

    return {
      sdkFeatures: Array.from(sdkFeatureSet),
      sdkDeviceFeatures: Array.from(sdkDeviceFeatureSet),
    };
  }, [sdkFeatureSets]);
  const currentMainTabKey = mainTabs[activeMainTab]?.key;

  const resetPartnerExpansionState = () => {
    setExpandedApplicationKey('');
    setExpandedPartnerKey('');
    setPartnerAppEditor(null);
    setPartnerAppActionError('');
  };

  const resetSdkExpansionState = () => {
    setExpandedSdkAppKey('');
    setExpandedSdkPartnerKey('');
    setExpandedSdkFeatureSetKey('');
    setSdkAppEditor(null);
    setSdkActionError('');
  };

  const toggleApplication = (rowKey) => {
    setExpandedApplicationKey((prev) => {
      const nextValue = prev === rowKey ? '' : rowKey;
      if (nextValue !== rowKey) {
        setPartnerAppEditor(null);
        setPartnerAppActionError('');
      }
      return nextValue;
    });
  };

  const togglePartner = (rowKey) => {
    setExpandedPartnerKey((prev) => {
      const nextValue = prev === rowKey ? '' : rowKey;
      if (nextValue !== rowKey) {
        setPartnerAppEditor(null);
        setPartnerAppActionError('');
      }
      return nextValue;
    });
  };

  const toggleSdkPartner = (rowKey) => {
    setExpandedSdkPartnerKey((prev) => (prev === rowKey ? '' : rowKey));
  };

  const toggleSdkApplication = (rowKey) => {
    setExpandedSdkAppKey((prev) => {
      const nextValue = prev === rowKey ? '' : rowKey;
      setExpandedSdkFeatureSetKey('');
      return nextValue;
    });
  };

  const toggleSdkFeatureSet = (rowKey) => {
    setExpandedSdkFeatureSetKey((prev) => (prev === rowKey ? '' : rowKey));
  };

  const handleToggleSdkPartnerActive = async (partner) => {
    if (!partner?.partnerId) return;

    setSdkActionError('');
    setSdkTogglePartnerId(partner.partnerId);

    const response = await API.deactivateOrActivateSDKPartner(token, partner.partnerId, !(partner.active === true));
    if (!response || response.status < 200 || response.status >= 300) {
      setSdkActionError(
        response?.data?.errorDescription || response?.data?.message || 'Failed to update SDK partner active state.'
      );
      setSdkTogglePartnerId('');
      return;
    }

    await fetchSdkPartners({ current: true });
    setSdkTogglePartnerId('');
  };

  const handleSaveSdkAppEditor = async () => {
    if (!sdkAppEditor?.form || !sdkAppEditor?.partnerId || !token) return;
    if (!isSDKAppValid(sdkAppEditor.form)) return;

    setSdkActionSaving(true);
    setSdkActionError('');

    const body = {
      partnerId: sdkAppEditor.partnerId,
      apiKey: sdkAppEditor.form.apiKey,
      bundleId: sdkAppEditor.form.bundleId.trim(),
      featureSetId: sdkAppEditor.form.featureSetId,
      enabled: sdkAppEditor.form.enabled,
      collectMeasurements: sdkAppEditor.form.collectMeasurements,
      collectTelemetry: sdkAppEditor.form.collectTelemetry,
    };

    const overrides = buildOverrides(sdkAppEditor.form);
    if (overrides) {
      body.overrides = overrides;
    }

    const response = sdkAppEditor.mode === 'add'
      ? await API.addAppToSDKPartner(token, body)
      : await API.updateSDKPartnerApp(token, body);

    if (!response || response.status < 200 || response.status >= 300) {
      setSdkActionError(
        response?.data?.errorDescription ||
        response?.data?.message ||
        `Failed to ${sdkAppEditor.mode === 'add' ? 'add' : 'update'} SDK app.`
      );
      setSdkActionSaving(false);
      return;
    }

    setSdkActionSaving(false);
    setSdkAppEditor(null);
    await fetchSdkPartners({ current: true });
  };

  const handleSdkOverrideChange = (type, overrideIndex, key, value) => {
    setSdkAppEditor((prev) => {
      if (!prev?.form) return prev;
      const fieldKey = type === 'add' ? 'addOverrides' : 'removeOverrides';
      const nextOverrides = (prev.form[fieldKey] || []).map((override, currentIndex) =>
        currentIndex === overrideIndex ? { ...override, [key]: value } : override
      );

      return {
        ...prev,
        form: {
          ...prev.form,
          [fieldKey]: nextOverrides,
        },
      };
    });
  };

  const handleSdkAddOverride = (type) => {
    setSdkAppEditor((prev) => {
      if (!prev?.form) return prev;
      const fieldKey = type === 'add' ? 'addOverrides' : 'removeOverrides';

      return {
        ...prev,
        form: {
          ...prev.form,
          [fieldKey]: [...(prev.form[fieldKey] || []), { sdkDeviceFeature: '', sdkFeature: '' }],
        },
      };
    });
  };

  const handleSavePartnerAppEditor = async (changedBody) => {
    if (!partnerAppEditor?.application?.applicationId || !token) return;

    if (!changedBody || Object.keys(changedBody).length === 0) {
      setPartnerAppEditor(null);
      setPartnerAppActionError('');
      return;
    }

    setPartnerAppActionSaving(true);
    setPartnerAppActionError('');

    const response = await API.updatePartnerApp(
      token,
      partnerAppEditor.application.applicationId,
      changedBody
    );

    if (!response || response.status < 200 || response.status >= 300) {
      setPartnerAppActionError(
        response?.data?.errorDescription ||
        response?.data?.message ||
        'Failed to update application.'
      );
      setPartnerAppActionSaving(false);
      return;
    }

    setPartnerAppActionSaving(false);
    setPartnerAppEditor(null);
    await fetchPartners({ current: true });
  };

  const openUsersPage = (row) => {
    if (!row?.application?.applicationId) return;

    props.history.push({
      pathname: `/manage-partners/application/${row.application.applicationId}/users`,
      state: {
        application: row.application,
        token,
        userInfo: props?.location?.state?.userInfo,
        returnState: props?.location?.state,
      },
    });
  };

  return (
    <div className="w-full px-4 py-4">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-xl font-semibold text-slate-900">Manage Partners</h2>
        {currentMainTabKey === 'partners' && activePartnersTab === 0 && (
          <span className={statusPillClass}>
            Page {partnersPage + 1} • {rows.length} partners
          </span>
        )}
        {currentMainTabKey === 'sdk' && activeSdkTab === 0 && (
          <span className={statusPillClass}>
            Page {sdkPartnersPage + 1} • {sdkRows.length} SDK partners
          </span>
        )}
      </div>

      {mainTabs.length > 0 && (
        <div className="mb-4 rounded-lg border border-slate-200 bg-white shadow-sm">
          <Tabs
            value={activeMainTab}
            onChange={(event, newValue) => setActiveMainTab(newValue)}
            indicatorColor="primary"
            textColor="primary"
          >
            {mainTabs.map((tab) => (
              <Tab key={tab.key} label={tab.label} />
            ))}
          </Tabs>
        </div>
      )}

      {currentMainTabKey === 'partners' && (
        <div className="mb-4 rounded-lg border border-slate-200 bg-white shadow-sm">
          <Tabs
            value={activePartnersTab}
            onChange={(event, newValue) => setActivePartnersTab(newValue)}
            indicatorColor="primary"
            textColor="primary"
          >
            <Tab label="Partner Integrations" />
            <Tab label="Onboard Partner" />
          </Tabs>
        </div>
      )}

      {currentMainTabKey === 'sdk' && (
        <div className="mb-4 rounded-lg border border-slate-200 bg-white shadow-sm">
          <Tabs
            value={activeSdkTab}
            onChange={(event, newValue) => setActiveSdkTab(newValue)}
            indicatorColor="primary"
            textColor="primary"
          >
            <Tab label="SDK Partner Integrations" />
            <Tab label="Onboard SDK Partner" />
          </Tabs>
        </div>
      )}

      {mainTabs.length === 0 && (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
          You do not have permission to manage partner integrations.
        </div>
      )}

      {currentMainTabKey === 'partners' && <TabPanel value={activePartnersTab} index={0}>
        {loading && (
          <div className="rounded-md border border-slate-200 bg-white p-4 text-sm text-slate-600">
            Loading partners...
          </div>
        )}

        {!loading && error && (
          <div className="rounded-md border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
            {error}
          </div>
        )}

        {!loading && !error && (
          <div className={tableShellClass}>
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead className={tableHeaderClass}>
                <tr>
                  <th className="px-3 py-2 text-left font-semibold text-slate-700">Partner Name</th>
                  <th className="px-3 py-2 text-left font-semibold text-slate-700">Applications</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {rows.map((row) => (
                  <React.Fragment key={row.rowKey}>
                    <tr className="hover:bg-slate-50">
                      <td className="whitespace-nowrap px-3 py-2 text-slate-900">
                        <button
                          type="button"
                          onClick={() => togglePartner(row.rowKey)}
                          className={linkButtonClass}
                        >
                          {row.partnerName}
                        </button>
                      </td>
                      <td className="px-3 py-2">
                        <div className="space-y-3">
                          {row.applications.length > 0 ? row.applications.map((application, index) => {
                            const applicationKey = `${row.rowKey}-${application?.applicationId || index}`;

                            return (
                              <div key={applicationKey} className="flex items-start gap-3">
                                <div className="h-16 w-16 shrink-0 overflow-hidden rounded-md border border-slate-200 bg-slate-100 p-1">
                                  {application?.logoURL ? (
                                    <img
                                      src={application.logoURL}
                                      alt={`${application?.name || 'Application'} logo`}
                                      className="h-full w-full object-contain"
                                    />
                                  ) : (
                                    <div className="flex h-full items-center justify-center text-[10px] text-slate-400">
                                      No Logo
                                    </div>
                                  )}
                                </div>
                                <div className="min-w-0">
                                  <button
                                    type="button"
                                    onClick={() => toggleApplication(applicationKey)}
                                    className={linkButtonClass}
                                  >
                                    {application?.name || '-'}
                                  </button>
                                  <div className="mt-1 max-w-xl text-sm leading-6 text-slate-600">{application?.description || '-'}</div>
                                </div>
                              </div>
                            );
                          }) : (
                            <div className="text-sm text-slate-500">No applications</div>
                          )}
                        </div>
                      </td>
                    </tr>

                    {expandedPartnerKey === row.rowKey && (
                      <tr className="bg-slate-50/60">
                        <td colSpan={2} className="px-3 py-3">
                          <PartnerDetails
                            details={row.partnerDetails}
                            onAddApplication={() => {
                              setApplicationPartner({
                                partnerId: row.partnerDetails.partnerId,
                                partnerName: row.partnerName,
                                userId: row.partnerDetails.userId,
                              });
                              setActiveMainTab(mainTabs.findIndex((tab) => tab.key === 'partners'));
                              setActivePartnersTab(1);
                            }}
                          />
                        </td>
                      </tr>
                    )}

                    {row.applications.map((application, index) => {
                      const applicationKey = `${row.rowKey}-${application?.applicationId || index}`;
                      if (expandedApplicationKey !== applicationKey) {
                        return null;
                      }

                      const applicationRow = { ...row, application };

                      return (
                        <tr key={`${applicationKey}-details`} className="bg-slate-50/60">
                          <td colSpan={2} className="px-3 py-3">
                            <ApplicationDetails
                              application={application || {}}
                              onUpdate={() => {
                                setPartnerAppActionError('');
                                setPartnerAppEditor({
                                  key: `${applicationKey}-update-application`,
                                  application,
                                });
                              }}
                              onShowUsers={() => openUsersPage(applicationRow)}
                            />
                          </td>
                        </tr>
                      );
                    })}

                    {row.applications.map((application, index) => {
                      const applicationKey = `${row.rowKey}-${application?.applicationId || index}`;

                      if (partnerAppEditor?.key !== `${applicationKey}-update-application`) {
                        return null;
                      }

                      return (
                        <tr key={`${applicationKey}-editor`} className="bg-slate-50/60">
                          <td colSpan={2} className="px-3 py-3">
                            <PartnerApplicationEditor
                              application={application || {}}
                              saving={partnerAppActionSaving}
                              error={partnerAppActionError}
                              onCancel={() => {
                                setPartnerAppEditor(null);
                                setPartnerAppActionError('');
                              }}
                              onSave={handleSavePartnerAppEditor}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </React.Fragment>
                ))}
              </tbody>
            </table>

            {rows.length === 0 && (
              <div className="px-4 py-6 text-sm text-slate-500">No partners found.</div>
            )}

            {(rows.length > 0 || partnersPage > 0) && (
              <div className="flex items-center justify-between border-t border-slate-200 px-4 py-3">
                <div className="text-sm text-slate-500">
                  Max {PAGE_SIZE} partners per page
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      resetPartnerExpansionState();
                      setPartnersPage((prev) => Math.max(prev - 1, 0));
                    }}
                    disabled={partnersPage === 0}
                    className={secondaryButtonClass}
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      resetPartnerExpansionState();
                      setPartnersPage((prev) => prev + 1);
                    }}
                    disabled={!partnersHasNextPage}
                    className={secondaryButtonClass}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </TabPanel>}

      {currentMainTabKey === 'partners' && <TabPanel value={activePartnersTab} index={1}>
        <OnboardPartner
          token={token}
          existingPartner={applicationPartner}
          onClose={async () => {
            setApplicationPartner(null);
            setActiveMainTab(mainTabs.findIndex((tab) => tab.key === 'partners'));
            setActivePartnersTab(0);
            await fetchPartners({ current: true });
          }}
        />
      </TabPanel>}

      {currentMainTabKey === 'sdk' && <TabPanel value={activeSdkTab} index={0}>
        {sdkLoading && (
          <div className="rounded-md border border-slate-200 bg-white p-4 text-sm text-slate-600">
            Loading SDK partners...
          </div>
        )}

        {!sdkLoading && sdkError && (
          <div className="rounded-md border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
            {sdkError}
          </div>
        )}

        {!sdkLoading && !sdkError && (
          <div className={tableShellClass}>
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead className={tableHeaderClass}>
                <tr>
                  <th className="px-3 py-2 text-left font-semibold text-slate-700">Partner Name</th>
                  <th className="px-3 py-2 text-left font-semibold text-slate-700">SDK Apps</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {sdkRows.map((row) => (
                  <React.Fragment key={row.rowKey}>
                    <tr className="hover:bg-slate-50">
                      <td className="whitespace-nowrap px-3 py-2 text-slate-900">
                        <button
                          type="button"
                          onClick={() => toggleSdkPartner(row.rowKey)}
                          className={linkButtonClass}
                        >
                          {row.partnerName}
                        </button>
                      </td>
                      <td className="px-3 py-2">
                        <div className="space-y-3">
                          {row.apps.length > 0 ? row.apps.map((app, index) => {
                            const appKey = `${row.rowKey}-${app?.apiKey || app?.bundleId || index}`;

                            return (
                              <div key={appKey} className="min-w-0">
                                <button
                                  type="button"
                                  onClick={() => toggleSdkApplication(appKey)}
                                  className={linkButtonClass}
                                >
                                  {app?.bundleId || '-'}
                                </button>
                              </div>
                            );
                          }) : (
                            <div className="text-sm text-slate-500">No SDK apps</div>
                          )}
                        </div>
                      </td>
                    </tr>

                    {expandedSdkPartnerKey === row.rowKey && (
                      <tr className="bg-slate-50/60">
                        <td colSpan={2} className="px-3 py-3">
                          <SDKPartnerDetails
                            details={row.partnerDetails}
                            onToggleActive={() => handleToggleSdkPartnerActive({
                              partnerId: row.partnerDetails.partnerId,
                              active: row.partnerDetails.active,
                            })}
                            onAddApp={() => {
                              setSdkActionError('');
                              setSdkAppEditor({
                                mode: 'add',
                                partnerId: row.partnerDetails.partnerId,
                                form: createSDKApp(),
                                key: `${row.rowKey}-add-app`,
                              });
                            }}
                            toggling={sdkTogglePartnerId === row.partnerDetails.partnerId}
                          />
                        </td>
                      </tr>
                    )}

                    {sdkAppEditor?.mode === 'add' && sdkAppEditor?.partnerId === row.partnerDetails.partnerId && (
                      <tr className="bg-slate-50/60">
                        <td colSpan={2} className="px-3 py-3">
                          <div className="rounded-md border border-slate-200 bg-white p-3">
                            <div className="mb-3 flex items-center justify-between gap-3">
                              <h4 className="text-sm font-semibold text-slate-900">Add SDK App</h4>
                              <button
                                type="button"
                                onClick={() => setSdkAppEditor(null)}
                                className={secondaryButtonClass}
                              >
                                Close
                              </button>
                            </div>
                            {sdkActionError && (
                              <div className="mb-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                                {sdkActionError}
                              </div>
                            )}
                            <SDKAppForm
                              app={sdkAppEditor.form}
                              featureSetIds={sdkFeatureSets.map((featureSet) => featureSet.featureSetId).filter(Boolean)}
                              sdkFeatures={sdkFeatureCatalog.sdkFeatures}
                              sdkDeviceFeatures={sdkFeatureCatalog.sdkDeviceFeatures}
                              onTextChange={(index, key, value) => {
                                setSdkAppEditor((prev) => ({ ...prev, form: { ...prev.form, [key]: value } }));
                              }}
                              onBooleanChange={(index, key, value) => {
                                setSdkAppEditor((prev) => ({ ...prev, form: { ...prev.form, [key]: value } }));
                              }}
                              onOverrideChange={(index, type, overrideIndex, key, value) => {
                                handleSdkOverrideChange(type, overrideIndex, key, value);
                              }}
                              onAddOverride={(index, type) => {
                                handleSdkAddOverride(type);
                              }}
                            />
                            <div className="mt-3 flex gap-2">
                              <button
                                type="button"
                                onClick={() => setSdkAppEditor(null)}
                                className={secondaryButtonClass}
                              >
                                Cancel
                              </button>
                              <button
                                type="button"
                                onClick={handleSaveSdkAppEditor}
                                disabled={!isSDKAppValid(sdkAppEditor.form) || sdkActionSaving}
                                className={primaryButtonClass}
                              >
                                {sdkActionSaving ? 'Saving...' : 'Add App'}
                              </button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}

                    {row.apps.map((app, index) => {
                      const appKey = `${row.rowKey}-${app?.apiKey || app?.bundleId || index}`;
                      const featureSetKey = `${appKey}-feature-set`;
                      const featureSet = sdkFeatureSetMap[app?.featureSetId];

                      return (
                        <React.Fragment key={`${appKey}-fragment`}>
                          {expandedSdkAppKey === appKey && (
                            <tr className="bg-slate-50/60">
                              <td colSpan={2} className="px-3 py-3">
                                <SDKAppDetails
                                  app={app || {}}
                                  onFeatureSetClick={() => toggleSdkFeatureSet(featureSetKey)}
                                  onUpdateApp={() => {
                                    setSdkActionError('');
                                    setSdkAppEditor({
                                      mode: 'update',
                                      partnerId: row.partnerDetails.partnerId,
                                      form: hydrateSDKApp(app),
                                      key: `${appKey}-update-app`,
                                    });
                                  }}
                                />
                              </td>
                            </tr>
                          )}

                          {sdkAppEditor?.mode === 'update' && sdkAppEditor?.key === `${appKey}-update-app` && (
                            <tr className="bg-slate-50/60">
                              <td colSpan={2} className="px-3 py-3">
                          <div className="rounded-md border border-slate-200 bg-white p-3">
                            <div className="mb-3">
                              <h4 className="text-sm font-semibold text-slate-900">Update SDK App</h4>
                            </div>
                                  {sdkActionError && (
                                    <div className="mb-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                                      {sdkActionError}
                                    </div>
                                  )}
                                  <SDKAppForm
                                    app={sdkAppEditor.form}
                                    featureSetIds={sdkFeatureSets.map((featureSet) => featureSet.featureSetId).filter(Boolean)}
                                    sdkFeatures={sdkFeatureCatalog.sdkFeatures}
                                    sdkDeviceFeatures={sdkFeatureCatalog.sdkDeviceFeatures}
                                    onTextChange={(index, key, value) => {
                                      setSdkAppEditor((prev) => ({ ...prev, form: { ...prev.form, [key]: value } }));
                                    }}
                                    onBooleanChange={(index, key, value) => {
                                      setSdkAppEditor((prev) => ({ ...prev, form: { ...prev.form, [key]: value } }));
                                    }}
                                    onOverrideChange={(index, type, overrideIndex, key, value) => {
                                      handleSdkOverrideChange(type, overrideIndex, key, value);
                                    }}
                                    onAddOverride={(index, type) => {
                                      handleSdkAddOverride(type);
                                    }}
                                  />
                                  <div className="mt-3 flex gap-2">
                                    <button
                                      type="button"
                                      onClick={() => setSdkAppEditor(null)}
                                      className={secondaryButtonClass}
                                    >
                                      Cancel
                                    </button>
                                    <button
                                      type="button"
                                      onClick={handleSaveSdkAppEditor}
                                      disabled={!isSDKAppValid(sdkAppEditor.form) || sdkActionSaving}
                                      className={primaryButtonClass}
                                    >
                                      {sdkActionSaving ? 'Saving...' : 'Update App'}
                                    </button>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}

                          {expandedSdkFeatureSetKey === featureSetKey && (
                            <tr className="bg-slate-50/60">
                              <td colSpan={2} className="px-3 py-3">
                                <SDKFeatureSetDetails
                                  featureSet={featureSet || { featureSetId: app?.featureSetId || '-', message: 'Feature set not found.', features: [] }}
                                />
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </React.Fragment>
                ))}
              </tbody>
            </table>

            {sdkRows.length === 0 && (
              <div className="px-4 py-6 text-sm text-slate-500">No SDK partners found.</div>
            )}

            {(sdkRows.length > 0 || sdkPartnersPage > 0) && (
              <div className="flex items-center justify-between border-t border-slate-200 px-4 py-3">
                <div className="text-sm text-slate-500">
                  Max {PAGE_SIZE} partners per page
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      resetSdkExpansionState();
                      setSdkPartnersPage((prev) => Math.max(prev - 1, 0));
                    }}
                    disabled={sdkPartnersPage === 0}
                    className={secondaryButtonClass}
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      resetSdkExpansionState();
                      setSdkPartnersPage((prev) => prev + 1);
                    }}
                    disabled={!sdkPartnersHasNextPage}
                    className={secondaryButtonClass}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </TabPanel>}

      {currentMainTabKey === 'sdk' && <TabPanel value={activeSdkTab} index={1}>
        <OnboardSDKPartner
          token={token}
          featureSets={sdkFeatureSets}
          sdkFeatures={sdkFeatureCatalog.sdkFeatures}
          sdkDeviceFeatures={sdkFeatureCatalog.sdkDeviceFeatures}
          onClose={async () => {
            setActiveMainTab(mainTabs.findIndex((tab) => tab.key === 'sdk'));
            setActiveSdkTab(0);
            await fetchSdkPartners({ current: true });
          }}
        />
      </TabPanel>}
    </div>
  );
};

export default ManagePartners;
